import { Composition, MAIN_SCENE, OptionValues } from './types/composition';
import { CatalogRoot, Material } from './types/catalog-internal';
import { CfgCondition, CfgConditionExpression, CfgOptionCondition } from './types/catalog-types';

export function conditionApplies(condition: CfgCondition | undefined, values: OptionValues, qualities: Material[], elementsSelected: string[] ): boolean {
    if(!condition) {
        return true;
    }

    if('elementSelected' in condition) {
        const elementIds = condition.elementSelected as string[];
        const elementMatch = elementIds.some(elementId => elementsSelected.includes(elementId));
        if(!elementMatch) {
            return false;
        }
    }

    for(const [key, matchingValues] of Object.entries(condition)) {
        if(key === 'elementSelected') {
            continue;
        }
        const value = values[key];
        if(!valueMatches(value, matchingValues as CfgConditionExpression, qualities)) {
            return false;
        }
    }
    return true;
}

export function optionsConditionApplies(condition: CfgOptionCondition | undefined, values: OptionValues, composition: Composition, catalog: CatalogRoot, elementsSelected: string[] ): boolean {
    if(!condition) {
        return true;
    }

    if('$elementOfGroups' in condition) {
        const groups = condition.$elementOfGroups;
        let groupMatch = false;

        for(const group of composition.groups) {
            if(group.scene !== MAIN_SCENE) {
                continue;
            }

            for(const element of group.elements) {
                const referenceElement = catalog.elements.find(e => e.id === element.element);
                if(referenceElement) {
                    if(referenceElement.group && groups.includes(referenceElement.group[0])) {
                        groupMatch = true;
                        break;
                    } else if(referenceElement.groups) {
                        for(const [elementGroup] of referenceElement.groups) {
                            if(groups.includes(elementGroup)) {
                                groupMatch = true;
                                break;
                            }
                        }
                    }
                }
            }
            if(groupMatch) {
                break;
            }
        }

        if(!groupMatch) {
            return false;
        }
    }

    // elementSelected means "catalog element id present in the composition",
    // not "currently selected instance". Scan the whole main scene.
    const compositionElementIds: string[] = [];
    for(const group of composition.groups) {
        if(group.scene !== MAIN_SCENE) {
            continue;
        }
        for(const element of group.elements) {
            compositionElementIds.push(element.element);
        }
    }
    const elementsForCheck = compositionElementIds.length > 0 ? compositionElementIds : elementsSelected;

    const { $elementOfGroups: _ignored, ...rest } = condition as CfgOptionCondition & Record<string, unknown>;
    return conditionApplies(rest as CfgCondition, values, catalog.qualities, elementsForCheck);
}

function valueMatches(value: string|undefined|null, condition: CfgConditionExpression, qualities: Material[]): boolean {
    if(typeof condition === 'string') {
        return value === condition;
    } else if(Array.isArray(condition)) {
        if(value === undefined || value === null) {
            return false;
        } else {
            return condition.includes(value);
        }
    } else {
        if(value === undefined || value === null) {
            return false;
        }
        const tag = condition.qualityHasTag;
        const [ qualityCode, colorCode ] = value.split('/');
        const quality = qualities.find(m => m.code === qualityCode);
        if(!quality) return false;
        return quality.tags.includes(tag);
    }
}
